import { queryOptions } from '@tanstack/react-query'
import type { GetAppSettingByCodeResult } from 'constellation-sdk'
import { apiFetch } from '@/lib/api/fetch'

/**
 * `GET /app-settings/{code}` answers `{ success, value }` — a FLAT value, not a
 * nested setting object.
 *
 * Imported from the SDK rather than hand-declared. It was hand-declared as
 * `{ appSetting: { value } }`, which type-checked perfectly and was simply
 * wrong: the selector below read `undefined` every time and silently fell
 * through to the relative fallback, so every QR preview in the app encoded a URL
 * with no host. Nothing failed — the codes just would not have scanned.
 */
type AppSettingResult = GetAppSettingByCodeResult

/**
 * The template the redirector serves short links on, e.g.
 * `https://cqrl.io/qr/<alias>`.
 *
 * A setting rather than a constant because the host differs per environment and
 * is what physically gets printed onto things. Cached hard — it changes roughly
 * never, and every QR preview on a 500-code grid depends on it.
 */
export const NOVA_LINK_TEMPLATE_CODE = 'nova_link_template'

export function novaLinkTemplateQuery() {
  return queryOptions({
    queryKey: ['app-settings', NOVA_LINK_TEMPLATE_CODE],
    queryFn: ({ signal }) =>
      apiFetch<AppSettingResult>(
        `/app-settings/${NOVA_LINK_TEMPLATE_CODE}`,
        { signal },
      ),
    staleTime: 60 * 60_000,
    gcTime: 24 * 60 * 60_000,
    select: (data): string =>
      /* A missing setting must not silently produce `undefined/<alias>` in a
         printed QR code. The relative form is visibly wrong instead. */
      data.value || '/qr/<alias>',
  })
}

/**
 * The runtime feature flag for the analytics wave (dashboard, saved views,
 * identities, the per-code scans ledger).
 *
 * `'false'` until it is flipped for everyone — see `useAnalyticsAccess`, which
 * also lets platform administrators through regardless so the feature can be
 * exercised in prod while this is off. Cached like the template: it changes
 * roughly never, and the shell reads it on every screen to decide the nav.
 */
export const ANALYTICS_ENABLED_CODE = 'analytics_enabled'

export function analyticsEnabledQuery() {
  return queryOptions({
    queryKey: ['app-settings', ANALYTICS_ENABLED_CODE],
    queryFn: ({ signal }) =>
      apiFetch<AppSettingResult>(
        `/app-settings/${ANALYTICS_ENABLED_CODE}`,
        { signal },
      ),
    staleTime: 15 * 60_000,
    gcTime: 24 * 60 * 60_000,
    /* Anything other than an explicit 'true' is off — a missing row, an empty
       value, or a stray string all keep the wall up. */
    select: (data): boolean => data.value === 'true',
  })
}
